import { useEffect, useRef, useState } from "react";
import { getSavedLocation, nowInLocation } from "@/lib/cities";
import { mapPanchang } from "@/lib/panchangLive";
import { publicPanchang, publicChoghadiya } from "@/lib/api";

// Standard last-resort coordinates — used ONLY when there is no saved city and
// browser geolocation is unavailable or denied. Precise device geolocation is
// always preferred so the almanac is computed for the viewer's exact spot.
const FALLBACK_LOC = { lat: 28.6139, lon: 77.2090, place: "New Delhi", tz: 330, tz_id: "Asia/Kolkata" };

const pad2 = (n) => String(n).padStart(2, "0");

// Rule 46: every value comes from the engine only. If it is unreachable, show the
// last engine result saved for this place + day (with a notice), never another
// calculation source. Small per-browser cache, newest 30 entries.
const CACHE_KEY = "dh_engine_cache_v1";
const cacheKeyFor = (target, loc) =>
  `${Number(loc.lat).toFixed(3)},${Number(loc.lon).toFixed(3)}|${target.year}-${pad2(target.month)}-${pad2(target.day)}`;
function readCache(key) {
  try { return (JSON.parse(localStorage.getItem(CACHE_KEY) || "{}"))[key] || null; } catch { return null; }
}
function saveCache(key, json) {
  try {
    const all = JSON.parse(localStorage.getItem(CACHE_KEY) || "{}");
    delete all[key];
    all[key] = { json, at: Date.now() };
    const keys = Object.keys(all);
    keys.slice(0, Math.max(0, keys.length - 30)).forEach((k) => delete all[k]);
    localStorage.setItem(CACHE_KEY, JSON.stringify(all));
  } catch { /* storage full/blocked: no cache */ }
}

// Resolve the target moment from the page's date/time controls, anchored to the
// SELECTED LOCATION's timezone (never the browser's). `loc` carries tz_id/tz so
// "now" is the city's wall-clock time.
function parseTarget(dateOverride, loc) {
  const city = nowInLocation(loc);
  const pad = (n) => String(n).padStart(2, "0");
  const cityToday = `${city.year}-${pad(city.month)}-${pad(city.day)}`;
  const cityHM = `${pad(city.hour)}:${pad(city.min)}`;
  if (dateOverride && dateOverride.includes("T")) {
    const [d, t] = dateOverride.split("T");
    const [year, month, day] = d.split("-").map(Number);
    const [hour, min] = t.split(":").map(Number);
    const isNow = d === cityToday && `${pad(hour)}:${pad(min)}` === cityHM;
    return { year, month, day, hour, min, isNow };
  }
  if (!dateOverride || dateOverride === cityToday) {
    return { ...city, isNow: true };
  }
  const [year, month, day] = dateOverride.split("-").map(Number);
  return { year, month, day, hour: 12, min: 0, isNow: false }; // noon for a chosen day
}

// Core panchang via our backend proxy (Rule 46 — never the engine directly); on
// failure, the last saved engine result.
async function loadPanchang(target, loc) {
  const { lat, lon, place } = loc;
  const body = { year: target.year, month: target.month, day: target.day, hour: target.hour, min: target.min, lat, lon, tz: loc.tz_id || "Asia/Kolkata" };
  const key = cacheKeyFor(target, loc);
  try {
    const json = await publicPanchang(body);
    if (json && json.error) throw new Error(json.error);
    saveCache(key, json);
    return mapPanchang(json, { place, target });
  } catch (e) {
    const hit = readCache(key);
    if (hit) return { ...mapPanchang(hit.json, { place, target }), source: "cache", cached_at: hit.at };
    throw new Error("Could not reach the Panchangam engine. Please try again shortly.");
  }
}

// Richer "lab" almanac (festivals, karmakala, varjyam, siddhi yogas, 16-slot
// choghadiya) via our backend proxy; takes an IANA tz string.
async function loadLab(target, loc) {
  const body = {
    year: target.year, month: target.month, day: target.day,
    hour: target.hour, min: target.min, sec: 0,
    lat: loc.lat, lon: loc.lon,
    tz: loc.tz_id || "Asia/Kolkata",
  };
  const json = await publicChoghadiya(body);
  if (json && json.error) throw new Error(json.error);
  return json;
}

// Live panchang + lab almanac for the chosen location (saved city → precise
// browser geolocation → standard fallback). Both requests are orchestrated in
// parallel; one failing never blanks the other. Auto-refreshes each minute while
// viewing "now"; `retry()` forces a fresh parallel fetch. Pass locKey to refetch
// on city change.
export function useLivePanchang(dateOverride, locKey, includeLab = true, fixedLoc = null) {
  const [data, setData] = useState(null);
  const [labData, setLabData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reload, setReload] = useState(0);
  const loc = useRef(FALLBACK_LOC);

  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      const target = parseTarget(dateOverride, loc.current);
      // The premium /lab almanac is fetched ONLY for premium subscribers; free
      // users never hit that endpoint, so no premium data ever reaches them.
      const tasks = [loadPanchang(target, loc.current)];
      if (includeLab) tasks.push(loadLab(target, loc.current));
      const results = await Promise.allSettled(tasks);
      const pRes = results[0];
      const lRes = includeLab ? results[1] : null;
      if (cancelled) return;
      if (pRes.status === "fulfilled") {
        setData({ ...pRes.value, is_live: !!target.isNow });
        setError(null);
      } else {
        setError(pRes.reason?.message || "Could not reach the panchang service.");
      }
      setLabData(lRes && lRes.status === "fulfilled" ? lRes.value : null);
      setLoading(false);
    };

    setLoading(true);
    // Fixed-location pages (City Panchangam) always compute for that exact city —
    // never the viewer's saved city or device GPS.
    if (fixedLoc && fixedLoc.lat != null) {
      loc.current = { lat: fixedLoc.lat, lon: fixedLoc.lon, place: fixedLoc.place || fixedLoc.name, tz: fixedLoc.tz, tz_id: fixedLoc.tz_id };
      run();
    } else {
      const saved = getSavedLocation();
      if (saved && saved.lat != null) {
        loc.current = { lat: saved.lat, lon: saved.lon, place: saved.name, tz: saved.tz, tz_id: saved.tz_id };
        run();
      } else if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          (pos) => {
            let tz_id;
            try { tz_id = Intl.DateTimeFormat().resolvedOptions().timeZone; } catch (_) { tz_id = "Asia/Kolkata"; }
            loc.current = { lat: pos.coords.latitude, lon: pos.coords.longitude, place: "Your location", tz_id };
            run();
          },
          () => { loc.current = FALLBACK_LOC; run(); },
          { enableHighAccuracy: true, timeout: 8000, maximumAge: 600000 }
        );
      } else {
        loc.current = FALLBACK_LOC;
        run();
      }
    }

    const id = setInterval(() => {
      if (parseTarget(dateOverride, loc.current).isNow) run();
    }, 60000);
    return () => { cancelled = true; clearInterval(id); };
  }, [dateOverride, locKey, reload, includeLab, fixedLoc?.lat, fixedLoc?.lon]);

  const retry = () => setReload((r) => r + 1);
  return { data, labData, loading, error, retry };
}
